/**
 * Country Context
 * Provides site-wide country selection (Canada or USA)
 * Persists selection in localStorage
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Country = 'CA' | 'US';

interface CountryContextType {
  country: Country;
  setCountry: (country: Country) => void;
  isCanada: boolean;
  isUSA: boolean;
  currencySymbol: string;
  currencyCode: string;
  countryName: string;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

const STORAGE_KEY = 'larrys_help_country';

interface CountryProviderProps {
  children: ReactNode;
}

export const CountryProvider: React.FC<CountryProviderProps> = ({ children }) => {
  const [country, setCountryState] = useState<Country>(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'US' || saved === 'CA') {
      return saved;
    }
    // Default to Canada
    return 'CA';
  });

  // Persist to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, country);
  }, [country]);

  const setCountry = (newCountry: Country) => {
    setCountryState(newCountry);
  };

  const value: CountryContextType = {
    country,
    setCountry,
    isCanada: country === 'CA',
    isUSA: country === 'US',
    currencySymbol: country === 'CA' ? '$' : '$',
    currencyCode: country === 'CA' ? 'CAD' : 'USD',
    countryName: country === 'CA' ? 'Canada' : 'United States',
  };

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = (): CountryContextType => {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export default CountryContext;
